// ABOUTME: Shared object types and data for webstones products
// ABOUTME: No external dependencies - safe to import from companion app

export enum ObjectType {
  Frame = "frame",
  Image = "image",
}

export interface ObjectBase {
  id: string;
  title: string;
  type: ObjectType;
  shopifyId: number;
  tagline: string;
  externalUrl?: string;
  // TODO: figure out how to do this
  additionalFields?: React.ReactNode[];
  objectUrl: string;
  live: boolean;
  objectClassName?: string;
  extraObjectUrls?: string[];
}

export interface FrameObject extends ObjectBase {
  type: ObjectType.Frame;
  iframeUrl: string;
}

export interface ImageObject extends ObjectBase {
  type: ObjectType.Image;
  actionImageUrl: string;
}

export type ComputingObject = FrameObject | ImageObject;

/**
 * PRODUCT CONFIGURATION GUIDE
 * ===========================
 *
 * This array defines all products/objects shown on the site. Each object can be either a physical
 * product with full e-commerce functionality or a display-only item.
 *
 * ADDING A NEW PRODUCT:
 * 1. Create a new object in this array with a unique `id`
 * 2. Set the appropriate `shopifyId` and `live` values (see below)
 * 3. Add required fields: title, tagline, objectUrl, type
 * 4. Optional: Add extraObjectUrls for additional images/videos
 *
 * KEY FIELD EXPLANATIONS:
 *
 * shopifyId & live combinations:
 * ┌─────────────┬────────────┬─────────────────────────────────────────────┐
 * │ shopifyId   │ live       │ Behavior                                    │
 * ├─────────────┼────────────┼─────────────────────────────────────────────┤
 * │ > 0         │ true       │ ✅ LIVE PRODUCT: Full e-commerce with       │
 * │             │            │ pricing, variants, add to cart, checkout    │
 * ├─────────────┼────────────┼─────────────────────────────────────────────┤
 * │ > 0         │ false      │ 🔄 COMING SOON: Shows product details but   │
 * │             │            │ hides price/cart, shows email signup       │
 * ├─────────────┼────────────┼─────────────────────────────────────────────┤
 * │ -1          │ false      │ 📱 DISPLAY ONLY: Shows basic info, no       │
 * │             │            │ Shopify integration, no purchase options    │
 * ├─────────────┼────────────┼─────────────────────────────────────────────┤
 * │ -1          │ true       │ ❌ INVALID: Don't use this combination       │
 * └─────────────┴────────────┴─────────────────────────────────────────────┘
 *
 * OTHER IMPORTANT FIELDS:
 *
 * • id: Unique identifier used in URLs (e.g., /product/{id})
 * • title: Product name displayed to users
 * • tagline: Short description shown in product grids
 * • objectUrl: Main product image (always required)
 * • extraObjectUrls: Additional images/videos for the carousel
 * • type: ObjectType.Frame (interactive) or ObjectType.Image (static)
 * • iframeUrl: Required for Frame types - the interactive experience URL
 * • actionImageUrl: Required for Image types - shows functionality/action
 * • externalUrl: Optional link to external sales page (overrides internal product page)
 * • objectClassName: Optional CSS class for custom styling
 *
 * SHOPIFY INTEGRATION:
 * When shopifyId > 0, the system automatically fetches:
 * • Product details, variants, pricing via GraphQL
 * • Handles inventory management and stock status
 * • Provides full checkout flow via Shopify Buy Button
 * • Supports direct variant linking via URL params (?variant=VariantName)
 *
 * EMAIL SIGNUPS:
 * • Coming Soon products (shopifyId > 0, live = false): "Sign up for early access"
 * • Sold Out products (when Shopify reports no stock): "Sign up for restock notification"
 */
export const Objects: ComputingObject[] = [
  {
    id: "fortune-webstones",
    title: "Fortune Webstone",
    type: ObjectType.Frame,
    iframeUrl: "https://shrine.computer/fortune",
    shopifyId: 8696775672061,
    tagline:
      "A ceramic fortune cookie that offers a new fortune every time you tap with your phone",
    objectUrl: "/objects/fortune-cookie.png",
    live: true,
    extraObjectUrls: [
      "/objects/extra/fortune-cookie-blue-object.png",
      "/objects/extra/fortune-webstone-green.png",
      "/objects/extra/fortune-webstone-white-black-object.png",
      "/objects/extra/fortune-webstones-batch-2_8805.png",
      "/objects/extra/yellow-fortune-cookie-whole.png",
    ],
  },
   {
    id: "phone-pillow",
    tagline: "A pillow for your phone to sleep on (activates Do Not Disturb)",
    title: "Phone Pillow",
    type: ObjectType.Image,
    actionImageUrl: "/actions/pillow-function.gif",
    objectUrl: "/objects/pillow.png",
    shopifyId: 8876410700029,
    live: true,
  },
  {
    id: "field-companions",
    title: "Field Companions",
    tagline: "A found rock that contains the sound of its home environment",
    type: ObjectType.Frame,
    iframeUrl: "https://shrine.computer/companions",
    objectUrl: "/objects/field-companion.png",
    live: true,
    externalUrl: "https://sc.metalabel.com/field-companions",
    shopifyId: 8768917307645,
    extraObjectUrls: [
      "/objects/extra/field-companions-individual-beach-transparent.png",
      "/objects/extra/field-companions-individual-garden-transparent.png",
      "/objects/extra/field-companions-individual-street-transparent.png",
    ],
  },
  {
    id: "router-temple",
    title: "Router Temple",
    tagline: "A router that offers guests connection to your home Internet",
    type: ObjectType.Image,
    actionImageUrl: "/actions/wifi-function.gif",
    objectUrl: "/objects/router-temple.png",
    shopifyId: 8940112183549,
    live: false,
    objectClassName: "router-temple",
  },
 
];

export function getObject(id: string): ComputingObject | null {
  return Objects.find((object) => object.id === id) || null;
}

export function getObjectByShopifyId(
  shopifyId: number
): ComputingObject | null {
  return Objects.find((object) => object.shopifyId === shopifyId) || null;
}
